import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { fieldToTextField } from 'formik-mui';
import { addCommas, removeCommas } from '@persian-tools/persian-tools';
import useFocusOnError from '../use-focus-on-error';
import { perToEnDigits } from 'utils/utils';
import { getIn } from 'formik';
import { useFloatingLabel } from 'components/inputs/use-floating-label';

export function PriceField({
    icon,
    secondIcon,
    liveValidation = true,
    ...rest
}: any): JSX.Element {
    const [showValue, updateShowValue] = useState('');
    const { field, form } = rest;
    const { name, value } = field;
    const {
        status,
        errors,
        touched,
        setStatus,
        handleChange,
        setFieldValue,
        validateField,
    } = form;
    const textRef = useRef();

    const fieldTouched = getIn(touched, name);
    const fieldError = getIn(errors, name);
    const fieldStatus = getIn(status, name);

    useEffect(() => {
        updateShowValue(addCommas(value));
    }, [value]);

    const handleFieldChange = useCallback(
        (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const _value = event.target.value;

            if (_value.trim() === '') {
                setFieldValue(name, '');
                handleChange(event);
                return;
            }

            const withoutCommas = removeCommas(_value);

            const isNumeric = /^[0-9۰-۹]*$/.test(String(withoutCommas));
            if (!isNumeric) return;

            const hasPersianDigits = /[۰-۹]/.test(String(withoutCommas));
            const cleanValue = hasPersianDigits
                ? perToEnDigits(String(withoutCommas))
                : String(withoutCommas);

            event.target.value = cleanValue;
            setFieldValue(name, cleanValue);
            handleChange(event);

            if (fieldStatus) {
                const { [name]: _, ...newStatus } = status;
                setStatus(newStatus);
            }
        },
        [status, name, setFieldValue, handleChange, fieldStatus],
    );

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!liveValidation) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => validateField(name), 500);
        return () => clearTimeout(timeoutRef.current!);
    }, [liveValidation, name, validateField, value]);

    useFocusOnError({
        fieldRef: textRef,
        name: field ? name : null,
    });

    const handleAutoFillFocus: any = useCallback((event: any) => {
        if (event.target.autocomplete) {
            event.target.autocomplete = 'whatever';
        }

        rest?.onFocus?.(event);
    }, [rest?.onFocus]);

    const floatingLabel = useFloatingLabel({
        value,
        onFocus: handleAutoFillFocus,
        onBlur: field?.onBlur,
    });

    return (
        <TextField
            variant="outlined"
            fullWidth
            {...fieldToTextField(rest as any)}
            value={showValue}
            onChange={handleFieldChange(name)}
            inputRef={textRef}
            onFocus={floatingLabel.onFocus}
            onBlur={floatingLabel.onBlur}
            autoComplete="off"
            error={
                Boolean(fieldTouched) &&
                Boolean(fieldError || Boolean(fieldStatus))
            }
            helperText={fieldTouched && (fieldError || fieldStatus)}
            InputLabelProps={{
                ...rest?.InputLabelProps,
                shrink: rest?.InputLabelProps?.shrink ?? floatingLabel.shrink,
            }}
            InputProps={{
                startAdornment: icon ? (
                    <InputAdornment position="start">{icon}</InputAdornment>
                ) : undefined,
                endAdornment: secondIcon ? (
                    <InputAdornment position="end">{secondIcon}</InputAdornment>
                ) : undefined,
                ...rest?.InputProps,
                autoComplete: 'off',
            }}
            sx={{
                '.MuiInputBase-input': {
                    direction: 'rtl',
                    textAlign: 'right',
                },
                ...rest?.sx,
            }}
            inputProps={{ maxLength: rest?.maxLength ?? undefined }}
        />
    );
}
